<template>
  <div class="app-shell">
    <OrganizationSetup @ready="orgReady = true" />

    <!-- Üst çubuk -->
    <header class="topbar">
      <div class="topbar-left">
        <button class="burger" @click="sidebarOpen = !sidebarOpen" aria-label="menu">
          <span></span><span></span><span></span>
        </button>
        <a class="brand" @click="go('main')">
          <img src="../assets/logo2.png" alt="Esignature Platform">
        </a>
      </div>

      <div class="topbar-right">
        <LanguageSwitcher class="is-hidden-mobile" />

        <div class="user-menu" :class="{ 'is-open': userMenuOpen }">
          <button class="user-trigger" @click.stop="userMenuOpen = !userMenuOpen">
            <avatar v-if="userName" :size="30" :username="userName"></avatar>
            <span class="user-name is-hidden-mobile">{{ userName }}</span>
            <span class="caret"></span>
          </button>

          <div class="user-dropdown" v-show="userMenuOpen">
            <p class="user-dropdown-head" v-if="userEmail">
              <strong>{{ userName }}</strong>
              <span>{{ userEmail }}</span>
            </p>
            <button class="user-dropdown-item" @click="go('profile')">
              <span class="icon"><i class="fa fa-user-o"></i></span>
              <span>{{ $t('nav.profile') }}</span>
            </button>
            <button class="user-dropdown-item" @click="go('org')">
              <span class="icon"><i class="fa fa-users"></i></span>
              <span>{{ $t('nav.organization') }}</span>
            </button>
            <div class="user-dropdown-sep"></div>
            <div class="user-dropdown-lang is-hidden-tablet">
              <LanguageSwitcher />
            </div>
            <button class="user-dropdown-item is-danger" @click="$store.dispatch('logOut')">
              <span class="icon"><i class="fa fa-power-off"></i></span>
              <span>{{ $t('nav.logout') }}</span>
            </button>
          </div>
        </div>
      </div>
    </header>

    <div class="app-body" v-if="orgReady">

      <!-- Yan menü -->
      <aside class="sidebar" :class="{ 'is-open': sidebarOpen }">
        <p class="side-label">{{ $t('nav.sectionWork') }}</p>
        <nav class="side-nav">
          <button v-for="item in workItems" :key="item.key"
                  class="side-link" :class="{ 'is-active': page === item.key }"
                  @click="go(item.key)">
            <span class="icon"><i class="fa" :class="item.icon"></i></span>
            <span>{{ $t('nav.' + item.key) }}</span>
          </button>
        </nav>

        <p class="side-label">{{ $t('nav.sectionAccount') }}</p>
        <nav class="side-nav">
          <button v-for="item in accountItems" :key="item.key"
                  class="side-link" :class="{ 'is-active': page === item.key }"
                  @click="go(item.key)">
            <span class="icon"><i class="fa" :class="item.icon"></i></span>
            <span>{{ $t('nav.' + item.labelKey) }}</span>
          </button>
        </nav>

        <div class="side-foot">
          <span class="version" v-if="version">v{{ version }}</span>
        </div>
      </aside>

      <!-- Yan menü açıkken arka planı kapat (mobil) -->
      <div class="sidebar-scrim" v-if="sidebarOpen" @click="sidebarOpen = false"></div>

      <!-- İçerik -->
      <main class="content">
        <div class="page-head">
          <div class="page-head-text">
            <p class="crumb">
              <a @click="go('main')">{{ $t('nav.home') }}</a>
              <template v-if="page !== 'main'">
                <span class="crumb-sep">/</span>
                <span>{{ pageTitle }}</span>
              </template>
            </p>
            <h1 class="page-title">{{ pageTitle }}</h1>
            <p class="page-sub">{{ pageSubtitle }}</p>
          </div>

          <div class="page-head-side">
            <!-- Yeni gönderim yalnızca sözleşmeler ekranında; ana menüde
                 ayrı bir madde olarak durmasına gerek yok -->
            
            <button class="btn-ghost" v-if="page === 'new'" @click="go('agreements')">
              <span class="icon"><i class="fa fa-arrow-left"></i></span>
              <span>{{ $t('nav.backToList') }}</span>
            </button>
            <span class="today" v-else>{{ today }}</span>
          </div>
        </div>

        <div class="page-body">
          <Intro v-if="page === 'main'" />
          <Templates v-else-if="page === 'templates'" />
          <Envelopes v-else-if="page === 'agreements'" @create="go('new')" />
          <NewEnvelope v-else-if="page === 'new'" @sent="go('agreements')" />
          <Verify v-else-if="page === 'verify'" />
          <Profile v-else-if="page === 'profile'" />
          <Organization v-else-if="page === 'org'" />
        </div>
      </main>
    </div>

    <!-- İstek sınırı -->
    <div class="modal" :class="{ 'is-active': isRateLimit }">
      <div class="modal-background" @click="isRateLimit = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('nav.rateLimitTitle') }}</p>
          <button class="delete" @click="isRateLimit = false"></button>
        </header>
        <section class="modal-card-body">{{ rateLimitMessage }}</section>
        <footer class="modal-card-foot">
          <button class="button is-danger" @click="isRateLimit = false">OK</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import Defaults from '../config/Defaults';
import Avatar from 'vue-avatar';
import Templates from './Templates.vue';
import Envelopes from './Envelopes.vue';
import NewEnvelope from './NewEnvelope.vue';
import Verify from './Verify.vue';
import Intro from './Intro.vue';
import OrganizationSetup from './OrganizationSetup.vue';
import Organization from './Organization.vue';
import Profile from './Profile.vue';
import LanguageSwitcher from './LanguageSwitcher.vue';

export default {
  name: 'Dashboard',
  components: {
    Avatar, Templates, Envelopes, NewEnvelope, Verify,
    Intro, OrganizationSetup, Organization, Profile, LanguageSwitcher
  },
  data() {
    return {
      page: 'main',
      orgReady: false,
      sidebarOpen: false,
      userMenuOpen: false,
      version: '',
      isRateLimit: false,
      rateLimitMessage: '',

      // Menü tanımları veriden geliyor; yeni sayfa eklemek tek satır
      workItems: [
        { key: 'templates', icon: 'fa-files-o' },
        { key: 'agreements', icon: 'fa-check-square-o' },
        { key: 'verify', icon: 'fa-shield' }
      ],
      accountItems: [
        { key: 'profile', labelKey: 'profile', icon: 'fa-user-o' },
        { key: 'org', labelKey: 'organization', icon: 'fa-building-o' }
      ]
    };
  },
  computed: {
    user() {
      const app = this.$store ? this.$store.state.the_app_data : null;
      return (app && app.the_user) ? app.the_user : null;
    },
    userName() {
      return this.user ? (this.user.fullName || this.user.email || '') : '';
    },
    userEmail() {
      return this.user ? this.user.email : '';
    },
    // Başlık ve alt başlık sayfa anahtarından türüyor; ayrı bir eşleme
    // tablosu tutmak, yeni sayfa eklerken unutulan bir yer demek
    pageTitle() {
      const map = { main: 'home', org: 'organization', new: 'newEnvelope' };
      return this.$t('nav.' + (map[this.page] || this.page));
    },
    pageSubtitle() {
      const map = { main: 'home', org: 'organization', new: 'newEnvelope' };
      return this.$t('nav.' + (map[this.page] || this.page) + 'Subtitle');
    },
    today() {
      return this.$formatDate(new Date(), false);
    }
  },
  mounted() {
    this.version = Defaults.applications().frontend.version;
    document.addEventListener('click', this.closeMenus);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenus);
  },
  methods: {
    go(page) {
      this.page = page;
      this.sidebarOpen = false;
      this.userMenuOpen = false;
      // Sayfa değişince içerik başa sarsın; uzun bir listeden gelince
      // yeni sayfanın ortasında açılmak kafa karıştırıyor
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    closeMenus() {
      this.userMenuOpen = false;
    }
  }
};
</script>

<style scoped>
/* --- Kabuk --- */
.app-shell {
  min-height: 100vh;
  background: #f6f7fb;
}

.app-body {
  display: flex;
  align-items: stretch;
  min-height: calc(100vh - 56px);
}

/* --- Üst çubuk --- */
.topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background: #1c1c33;
  color: #e7e9f2;
}

.topbar-left,
.topbar-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.brand {
  display: flex;
  align-items: center;
}

.brand img {
  max-height: 30px;
  display: block;
}

.burger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  width: 34px;
  height: 34px;
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 0 6px;
}

.burger span {
  display: block;
  height: 2px;
  background: #aeb4cc;
  border-radius: 2px;
}

/* --- Kullanıcı menüsü --- */
.user-menu {
  position: relative;
}

.user-trigger {
  display: flex;
  align-items: center;
  gap: 9px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  padding: 4px 12px 4px 4px;
  color: #e7e9f2;
  cursor: pointer;
  transition: background-color 0.15s;
}

.user-trigger:hover,
.is-open .user-trigger {
  background: rgba(255, 255, 255, 0.09);
}

.user-name {
  font-size: 13px;
  font-weight: 600;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.caret {
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid currentColor;
  opacity: 0.7;
}

.user-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 232px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(20, 20, 40, 0.2);
  padding: 6px;
  color: #363636;
}

.user-dropdown-head {
  padding: 10px 12px 12px;
  border-bottom: 1px solid #eef0f5;
  margin-bottom: 6px;
  line-height: 1.4;
}

.user-dropdown-head strong {
  display: block;
  font-size: 13px;
  color: #22283a;
}

.user-dropdown-head span {
  font-size: 12px;
  color: #8b93a8;
  word-break: break-all;
}

.user-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: transparent;
  border: 0;
  border-radius: 7px;
  padding: 9px 12px;
  font-size: 13.5px;
  color: #3c4257;
  cursor: pointer;
  text-align: left;
}

.user-dropdown-item:hover {
  background: #f3f5fa;
}

.user-dropdown-item.is-danger {
  color: #c4413c;
}

.user-dropdown-item.is-danger:hover {
  background: #fdf1f0;
}

.user-dropdown-item .icon {
  color: #98a0b5;
  width: 16px;
}

.user-dropdown-item.is-danger .icon {
  color: #c4413c;
}

.user-dropdown-sep {
  height: 1px;
  background: #eef0f5;
  margin: 6px 4px;
}

.user-dropdown-lang {
  padding: 8px 12px;
}

/* --- Yan menü --- */
.sidebar {
  flex: 0 0 232px;
  background: #22223d;
  padding: 22px 12px 18px;
  display: flex;
  flex-direction: column;
}

.side-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #6f7699;
  padding: 0 12px;
  margin: 18px 0 8px;
}

.side-label:first-child {
  margin-top: 0;
}

.side-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.side-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  background: transparent;
  border: 0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  color: #b3b9d4;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s, color 0.15s;
}

.side-link:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #e7e9f2;
}

/* Aktif maddeyi renkle değil, solundaki çubukla işaretliyoruz:
   göz menüyü tararken konumu şekilden daha hızlı yakalıyor */
.side-link.is-active {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-weight: 600;
}

.side-link.is-active::before {
  content: "";
  position: absolute;
  left: -12px;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: #00d1b2;
}

.side-link .icon {
  width: 18px;
  color: inherit;
  opacity: 0.85;
}

.side-foot {
  margin-top: auto;
  padding: 16px 12px 0;
}

.version {
  font-size: 11px;
  color: #5d6485;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
}

.sidebar-scrim {
  display: none;
}

/* --- İçerik --- */
.content {
  flex: 1 1 auto;
  min-width: 0;
  padding: 28px 34px 56px;
}

.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e6e8f0;
  margin-bottom: 24px;
}

.crumb {
  font-size: 12px;
  color: #98a0b5;
  margin-bottom: 6px;
}

.crumb a {
  color: #98a0b5;
  cursor: pointer;
}

.crumb a:hover {
  color: #5a6180;
}

.crumb-sep {
  margin: 0 7px;
  opacity: 0.6;
}

.page-title {
  font-size: 25px;
  font-weight: 700;
  color: #1e2233;
  line-height: 1.2;
}

.page-sub {
  font-size: 14px;
  color: #79809a;
  margin-top: 5px;
  max-width: 620px;
}

.page-head-side {
  flex: 0 0 auto;
  padding-top: 18px;
}

.today {
  font-size: 12.5px;
  color: #98a0b5;
  font-variant-numeric: tabular-nums;
}

.btn-primary,
.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.btn-primary {
  background: #22223d;
  border: 1px solid #22223d;
  color: #fff;
}

.btn-primary:hover {
  background: #2e2e52;
}

.btn-ghost {
  background: #fff;
  border: 1px solid #d9dde8;
  color: #3c4257;
}

.btn-ghost:hover {
  border-color: #b9c0d2;
}

.page-body {
  min-height: 40vh;
}

/* Alt bileşenler kendi section/container'larını taşıyor; kabuk zaten
   boşluk verdiği için onları sıfırlıyoruz */
.page-body >>> .section {
  padding: 0;
}

.page-body >>> .container {
  margin: 0;
}

/* --- Duyarlı --- */
@media screen and (max-width: 1023px) {
  .burger {
    display: flex;
  }

  .sidebar {
    position: fixed;
    top: 56px;
    bottom: 0;
    left: 0;
    z-index: 25;
    width: 250px;
    transform: translateX(-100%);
    transition: transform 0.2s ease;
  }

  .sidebar.is-open {
    transform: translateX(0);
  }

  .sidebar-scrim {
    display: block;
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(18, 18, 34, 0.45);
    z-index: 20;
  }

  .content {
    padding: 22px 18px 44px;
  }

  .page-head {
    flex-direction: column;
    align-items: stretch;
  }

  .page-head-side {
    padding-top: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar {
    transition: none;
  }
}
</style>


// WEBPACK FOOTER //
// src/Components/Dashboard.vue